import React, { useEffect, useMemo, useState } from "react";
import { Line, Scatter } from "react-chartjs-2";
import { Alert, Button, DatePicker, Popover, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { COLORS, UI_DATE_FORMAT } from "../../../consts";
import dayjs from "dayjs";
import { useUser } from "../../../context/UserContext";
import { cmToInches } from "../../../utils/cmToInchesConversion";

const graphColors = COLORS.graphColors;
const { RangePicker } = DatePicker;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToRgb = (hexColor) => {
  const hex = hexColor?.replace("#", "");
  if (!hex || (hex.length !== 3 && hex.length !== 6)) return null;

  const normalized =
    hex.length === 3
      ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
      : hex;

  const parsed = Number.parseInt(normalized, 16);
  if (Number.isNaN(parsed)) return null;

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

const toHeatColor = (baseColor, value, maxValue) => {
  const { r, g, b } = hexToRgb(baseColor) || {
    r: 24,
    g: 144,
    b: 255,
  };
  const safeMax = maxValue > 0 ? maxValue : 1;
  const ratio = clamp(value / safeMax, 0, 1);
  const alpha = 0.08 + ratio * 0.92;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const parseBinRange = (label) => {
  const rawLabel = String(label).trim();
  if (rawLabel.includes("-")) {
    const [startRaw, endRaw] = rawLabel.split("-");
    const start = Number(startRaw);
    const end = Number(endRaw);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      const lower = Math.min(start, end);
      const upper = Math.max(start, end);
      return { start: lower, end: upper > lower ? upper : lower + 1 };
    }
  }

  const start = Number(rawLabel);
  if (!Number.isFinite(start)) return null;
  return { start, end: start + 1 };
};

const getQuantileFromDistribution = (binRanges, percentages, quantile) => {
  const safePercentages = percentages.map((value) =>
    Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0,
  );
  const total = safePercentages.reduce((sum, value) => sum + value, 0);
  if (total <= 0 || binRanges.length === 0) return null;

  const target = total * clamp(quantile, 0, 1);
  let cumulative = 0;

  for (let i = 0; i < binRanges.length; i += 1) {
    const binRange = binRanges[i];
    if (!binRange) continue;
    const current = safePercentages[i];
    const next = cumulative + current;
    if (next >= target) {
      if (current <= 0) return binRange.start;
      const ratio = clamp((target - cumulative) / current, 0, 1);
      return binRange.start + (binRange.end - binRange.start) * ratio;
    }
    cumulative = next;
  }

  for (let i = binRanges.length - 1; i >= 0; i -= 1) {
    if (binRanges[i]) return binRanges[i].end;
  }
  return null;
};

const getModeFromDistribution = (binRanges, percentages) => {
  if (!binRanges.length || !percentages.length) return null;
  let maxIndex = 0;
  for (let i = 1; i < percentages.length; i += 1) {
    if ((percentages[i] ?? 0) > (percentages[maxIndex] ?? 0)) {
      maxIndex = i;
    }
  }
  const binRange = binRanges[maxIndex];
  if (!binRange) return null;
  return (binRange.start + binRange.end) / 2;
};

const getQuantileFromValues = (values, quantile) => {
  const numericValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (!numericValues.length) return null;

  const q = clamp(quantile, 0, 1);
  const position = (numericValues.length - 1) * q;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);

  if (lowerIndex === upperIndex) return numericValues[lowerIndex];

  const lowerValue = numericValues[lowerIndex];
  const upperValue = numericValues[upperIndex];
  const ratio = position - lowerIndex;
  return lowerValue + (upperValue - lowerValue) * ratio;
};

const getSpecSplitPercentages = (dataset, specBounds, binRanges) => {
  const rawSizes = (dataset.rawSizes || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (rawSizes.length > 0) {
    const under = rawSizes.filter((value) => value < specBounds.lower).length;
    const inside = rawSizes.filter(
      (value) => value >= specBounds.lower && value <= specBounds.upper,
    ).length;
    const over = rawSizes.filter((value) => value > specBounds.upper).length;
    const total = rawSizes.length;
    return {
      under: (under / total) * 100,
      inside: (inside / total) * 100,
      over: (over / total) * 100,
    };
  }

  const percentages = dataset.data?.slice(0, binRanges.length) || [];
  let under = 0;
  let inside = 0;
  let over = 0;

  percentages.forEach((value, index) => {
    const numericValue = Number.isFinite(Number(value)) ? Number(value) : 0;
    const binRange = binRanges[index];
    if (!binRange) return;
    const center = (binRange.start + binRange.end) / 2;
    if (center < specBounds.lower) under += numericValue;
    else if (center > specBounds.upper) over += numericValue;
    else inside += numericValue;
  });

  const total = under + inside + over;
  if (total <= 0) return { under: 0, inside: 0, over: 0 };

  return {
    under: (under / total) * 100,
    inside: (inside / total) * 100,
    over: (over / total) * 100,
  };
};

const DistributionGraph = ({
  BIN_END,
  isImperial = false,
  graphData,
  dateList,
  minSize,
  maxSize,
  visibility,
  setVisibility,
  showPercentage,
  setShowPercentage,
  filteredGraphData,
  setFilteredGraphData,
  setAverageData,
  isFullscreen = false,
}) => {
  const DATE_FORMAT = "YYYY-MM-DD";
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  const KEY_IN_DATA = "broccoli_lengths_cm";
  const [graphType, setGraphType] = useState("line");
  const { userData } = useUser();
  const isSuperUser = Boolean(userData?.is_superuser);

  const [selectedDates, setSelectedDates] = useState([]);

  const [options, setOptions] = useState({
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw.toFixed(2)}%`;
          },
        },
      },
      greenBandPlugin: {
        show: false,
        minSize: minSize,
        maxSize: maxSize,
        color: "rgba(0, 255, 0, 0.2)",
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
          font: {
            size: 16,
          },
        },
        grid: { display: false },
        ticks: {
          autoSkip: false,
          font: {
            size: 14,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: "Percentage of Broccoli heads",
          font: {
            size: 16,
          },
        },
        grid: { display: false },
        beginAtZero: false,
        ticks: {
          autoSkip: false,
          font: {
            size: 14,
          },
        },
      },
    },
  });

  const distributionLabels = Array.from(
    { length: BIN_END },
    (_, i) => `${isImperial ? (i * 0.5) : i}`
  );

  const chartDatasets = filteredGraphData.map((dataset) => {
    return {
      ...dataset,
      type: "line",
      tension: 0.3,
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 4,
      hidden: !visibility[dataset.label],
    };
  });

  const chartData = { labels: distributionLabels, datasets: chartDatasets };

  const chartOptions = options;

  const graphTypeOptions = [
    { label: "Line", value: "line" },
    { label: "Heatmap", value: "heatmap" },
    { label: "Quantile Trend", value: "quantile" },
    { label: "Spec Mix (100%)", value: "specMix" },
    { label: "ECDF Trend", value: "ecdf" },
    { label: "Delta Trend", value: "delta" },
    { label: "In-Spec KPI Trend", value: "kpi" },
  ];

  const graphInfoByType = {
    line: {
      short:
        "Line Distribution: Compare how the full size distribution shape shifts by date.",
      details: [
        "Each line is one date.",
        "Y values are per-bin percentages: (bin count / total heads on that date) * 100.",
      ],
      relevance:
        "Best for seeing peak movement, spread, and tail behavior across forecast dates.",
    },
    heatmap: {
      short:
        "Heatmap: Quick density scan across dates and size bins (higher opacity = higher percentage).",
      details: [
        "Rows represent dates; columns represent size bins.",
        "Cell intensity is normalized by that view's max bin percentage.",
      ],
      relevance:
        "Best for quickly spotting where concentration bands move over time.",
    },
    quantile: {
      short:
        "Quantile Trend: Tracks P25, P50 (median), P75, and mode over dates.",
      details: [
        "P25/P50/P75 are computed from raw sizes (sorted + interpolated quantiles).",
        "Mode is estimated from the highest histogram bin midpoint.",
      ],
      relevance:
        "Best for median drift and spread changes without reading every distribution curve.",
    },
    specMix: {
      short:
        "Spec Mix (100%): Shows under-spec, in-spec, and over-spec share by date.",
      details: [
        "Primary calculation uses raw sizes against current spec bounds.",
        "Fallback uses histogram-bin center approximation when raw sizes are unavailable.",
      ],
      relevance: "Best for operational quality split tracking over time.",
    },
    ecdf: {
      short: "ECDF Trend: Cumulative distribution by size for each date.",
      details: [
        "For each date: cumulative sum of bin percentages, normalized to 0-100%.",
        "At any x, the curve shows % of heads <= that size.",
      ],
      relevance:
        "Best for percentile reading and direct stochastic dominance comparison between dates.",
    },
    delta: {
      short:
        "Delta Trend: Shows median shift and IQR-width shift relative to first visible date.",
      details: [
        "Median shift = P50(date) - P50(baseline).",
        "IQR width shift = (P75-P25)(date) - (P75-P25)(baseline).",
      ],
      relevance:
        "Best for quantifying movement direction and spread change with a stable baseline.",
    },
    kpi: {
      short: "In-Spec KPI Trend: Single KPI trend for in-spec percentage.",
      details: [
        "In-spec % = count(lower <= size <= upper) / total * 100 from raw sizes.",
        "Uses same spec bounds as the Spec Mix view.",
      ],
      relevance: "Best for quick decision dashboards and target tracking.",
    },
  };
  const activeGraphInfo = graphInfoByType[graphType] || graphInfoByType.line;

  const visibleDatasets = useMemo(
    () => filteredGraphData.filter((dataset) => visibility[dataset.label] !== false),
    [filteredGraphData, visibility],
  );

  const rowColorByLabel = useMemo(
    () =>
      visibleDatasets.reduce((acc, dataset, index) => {
        acc[dataset.label] =
          dataset.borderColor ||
          dataset.backgroundColor ||
          graphColors[index % graphColors.length];
        return acc;
      }, {}),
    [visibleDatasets],
  );

  const heatmapMaxValue = useMemo(() => {
    let maxValue = 0;
    visibleDatasets.forEach((dataset) => {
      dataset.data?.slice(0, distributionLabels.length).forEach((value) => {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
          maxValue = Math.max(maxValue, numericValue);
        }
      });
    });
    return maxValue;
  }, [visibleDatasets, distributionLabels.length]);

  const heatmapData = useMemo(
    () =>
      visibleDatasets.flatMap((dataset) =>
        dataset.data?.slice(0, distributionLabels.length).map((value, binIndex) => ({
          x: distributionLabels[binIndex],
          y: dataset.label,
          v: Number.isFinite(Number(value)) ? Number(value) : 0,
        })),
      ),
    [visibleDatasets, distributionLabels],
  );

  const heatmapOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: () => "Distribution Density",
            label: (tooltipItem) => {
              const { x, y, v } = tooltipItem.raw || {};
              const unit = isImperial ? "in" : "cm";
              return `${y} | ${x} ${unit}: ${(v || 0).toFixed(2)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "category",
          labels: distributionLabels,
          title: {
            display: true,
            text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 14 },
          },
        },
        y: {
          type: "category",
          labels: visibleDatasets.map((dataset) => dataset.label),
          reverse: true,
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
      },
    }),
    [distributionLabels, isFullscreen, isImperial, visibleDatasets],
  );

  const heatmapChartData = useMemo(
    () => ({
      datasets: [
        {
          label: "Distribution Density",
          data: heatmapData,
          showLine: false,
          pointStyle: "rectRounded",
          borderWidth: 0,
          pointRadius: (context) => {
            const chartArea = context.chart?.chartArea;
            if (!chartArea) return 6;
            const xCount = Math.max(distributionLabels.length, 1);
            const yCount = Math.max(visibleDatasets.length, 1);
            const cellWidth = chartArea.width / xCount;
            const cellHeight = chartArea.height / yCount;
            return Math.max(2, Math.min(cellWidth, cellHeight) / 2 - 1);
          },
          pointHoverRadius: 8,
          backgroundColor: (context) => {
            const value = Number(context.raw?.v) || 0;
            const rowLabel = context.raw?.y;
            const rowColor = rowColorByLabel[rowLabel] || COLORS.primaryBlue;
            return toHeatColor(rowColor, value, heatmapMaxValue);
          },
        },
      ],
    }),
    [
      distributionLabels.length,
      heatmapData,
      heatmapMaxValue,
      rowColorByLabel,
      visibleDatasets.length,
    ],
  );

  const binRanges = useMemo(
    () => distributionLabels.map((label) => parseBinRange(label)),
    [distributionLabels],
  );

  const quantileChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const p25 = [];
    const p50 = [];
    const p75 = [];
    const mode = [];

    visibleDatasets.forEach((dataset) => {
      const percentages = dataset.data?.slice(0, binRanges.length) || [];
      const rawSizes = dataset.rawSizes || [];
      const q25 = getQuantileFromValues(rawSizes, 0.25);
      const q50 = getQuantileFromValues(rawSizes, 0.5);
      const q75 = getQuantileFromValues(rawSizes, 0.75);

      p25.push(
        q25 !== null
          ? q25
          : getQuantileFromDistribution(binRanges, percentages, 0.25),
      );
      p50.push(
        q50 !== null
          ? q50
          : getQuantileFromDistribution(binRanges, percentages, 0.5),
      );
      p75.push(
        q75 !== null
          ? q75
          : getQuantileFromDistribution(binRanges, percentages, 0.75),
      );
      mode.push(getModeFromDistribution(binRanges, percentages));
    });

    return {
      labels,
      datasets: [
        {
          label: "P25",
          data: p25,
          borderColor: "rgba(59, 130, 246, 0.4)",
          backgroundColor: "rgba(59, 130, 246, 0.10)",
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
        {
          label: "P75",
          data: p75,
          borderColor: "rgba(59, 130, 246, 0.4)",
          backgroundColor: "rgba(59, 130, 246, 0.10)",
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: "-1",
        },
        {
          label: "P50 (Median)",
          data: p50,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          borderWidth: 2.5,
          pointRadius: 3,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
        {
          label: "Mode",
          data: mode,
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }, [binRanges, visibleDatasets]);

  const quantileOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)} ${isImperial ? "in" : "cm"}`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
        y: {
          title: {
            display: true,
            text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          beginAtZero: true,
          ticks: {
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen, isImperial],
  );

  const specBounds = useMemo(
    () => ({
      lower: Math.min(Number(minSize) || 0, Number(maxSize) || 0),
      upper: Math.max(Number(minSize) || 0, Number(maxSize) || 0),
    }),
    [minSize, maxSize],
  );

  const ecdfChartData = useMemo(
    () => ({
      labels: distributionLabels,
      datasets: filteredGraphData.map((dataset, index) => {
        const percentages = dataset.data?.slice(0, binRanges.length) || [];
        const safePercentages = percentages.map((value) =>
          Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0,
        );
        const total = safePercentages.reduce((sum, value) => sum + value, 0);
        let cumulative = 0;
        const cdf = safePercentages.map((value) => {
          cumulative += value;
          return total > 0 ? (cumulative / total) * 100 : 0;
        });

        return {
          label: dataset.label,
          data: cdf,
          borderColor:
            dataset.borderColor ||
            dataset.backgroundColor ||
            graphColors[index % graphColors.length],
          backgroundColor:
            dataset.backgroundColor ||
            dataset.borderColor ||
            graphColors[index % graphColors.length],
          tension: 0.25,
          borderWidth: 2,
          pointRadius: 1.5,
          pointHoverRadius: 4,
          hidden: !visibility[dataset.label],
          fill: false,
          spanGaps: true,
        };
      }),
    }),
    [binRanges.length, distributionLabels, filteredGraphData, visibility],
  );

  const ecdfOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 14 } },
        },
        y: {
          title: {
            display: true,
            text: "Cumulative Share (%)",
            font: { size: 16 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen, isImperial],
  );

  const specMixChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const underSpec = [];
    const inSpec = [];
    const overSpec = [];

    visibleDatasets.forEach((dataset) => {
      const split = getSpecSplitPercentages(dataset, specBounds, binRanges);
      underSpec.push(split.under);
      inSpec.push(split.inside);
      overSpec.push(split.over);
    });

    return {
      labels,
      datasets: [
        {
          label: "Under Spec",
          data: underSpec,
          borderColor: "#f97316",
          backgroundColor: "rgba(249, 115, 22, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
        {
          label: "In Spec",
          data: inSpec,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
        {
          label: "Over Spec",
          data: overSpec,
          borderColor: "#6366f1",
          backgroundColor: "rgba(99, 102, 241, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
      ],
    };
  }, [binRanges, specBounds, visibleDatasets]);

  const specMixOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
        y: {
          title: {
            display: true,
            text: "Share (%)",
            font: { size: 16 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          stacked: true,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen],
  );

  const deltaChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const metrics = visibleDatasets.map((dataset) => {
      const percentages = dataset.data?.slice(0, binRanges.length) || [];
      const rawSizes = dataset.rawSizes || [];
      const q25 =
        getQuantileFromValues(rawSizes, 0.25) ??
        getQuantileFromDistribution(binRanges, percentages, 0.25);
      const q50 =
        getQuantileFromValues(rawSizes, 0.5) ??
        getQuantileFromDistribution(binRanges, percentages, 0.5);
      const q75 =
        getQuantileFromValues(rawSizes, 0.75) ??
        getQuantileFromDistribution(binRanges, percentages, 0.75);
      return {
        median: Number.isFinite(q50) ? q50 : null,
        iqr:
          Number.isFinite(q25) && Number.isFinite(q75) ? q75 - q25 : null,
      };
    });

    const baseline = metrics.find(
      (item) => Number.isFinite(item.median) && Number.isFinite(item.iqr),
    );
    const baselineMedian = baseline?.median ?? 0;
    const baselineIqr = baseline?.iqr ?? 0;

    return {
      labels,
      datasets: [
        {
          label: isImperial ? "Median Shift (in)" : "Median Shift (cm)",
          data: metrics.map((item) =>
            Number.isFinite(item.median) ? item.median - baselineMedian : null,
          ),
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          borderWidth: 2.5,
          pointRadius: 2.5,
          tension: 0.3,
          spanGaps: true,
          fill: false,
        },
        {
          label: isImperial ? "IQR Width Shift (in)" : "IQR Width Shift (cm)",
          data: metrics.map((item) =>
            Number.isFinite(item.iqr) ? item.iqr - baselineIqr : null,
          ),
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 2,
          tension: 0.3,
          spanGaps: true,
          fill: false,
        },
        {
          label: "Baseline",
          data: labels.map(() => 0),
          borderColor: "rgba(107, 114, 128, 0.55)",
          backgroundColor: "rgba(107, 114, 128, 0.55)",
          borderDash: [3, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0,
          fill: false,
        },
      ],
    };
  }, [binRanges, isImperial, visibleDatasets]);

  const deltaOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)} ${isImperial ? "in" : "cm"}`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 12 } },
        },
        y: {
          title: {
            display: true,
            text: isImperial ? "Change From Baseline (in)" : "Change From Baseline (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { font: { size: 14 } },
        },
      },
    }),
    [isFullscreen, isImperial],
  );

  const kpiChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const inSpecSeries = visibleDatasets.map(
      (dataset) => getSpecSplitPercentages(dataset, specBounds, binRanges).inside,
    );

    return {
      labels,
      datasets: [
        {
          label: "In Spec (%)",
          data: inSpecSeries,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderWidth: 2.5,
          pointRadius: 2.5,
          tension: 0.3,
          fill: true,
          spanGaps: true,
        },
      ],
    };
  }, [binRanges, specBounds, visibleDatasets]);

  const kpiOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 12 } },
        },
        y: {
          title: {
            display: true,
            text: "In Spec Share (%)",
            font: { size: 16 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen],
  );

  const greenBandPlugin = {
    id: "greenBandPlugin",
    beforeDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;
      const xScale = chart.scales.x;

      if (options.show) {
        const minPixel = xScale.getPixelForValue(options.minSize);
        const maxPixel = xScale.getPixelForValue(options.maxSize);
        ctx.fillStyle = options.color;
        ctx.fillRect(
          minPixel,
          chartArea.top,
          maxPixel - minPixel,
          chartArea.bottom - chartArea.top,
        );
      }
    },
  };

  useEffect(() => {
    // Convert size values to chart scale positions
    // In metric mode: size is already in cm, matches chart x-axis (0-25)
    // In imperial mode: size is in inches, chart x-axis is in 0.5-inch bins (0-50)
    let chartMinSize = minSize;
    let chartMaxSize = maxSize;

    if (isImperial) {
      // Convert inches to bin positions (each bin = 0.5 inches)
      chartMinSize = minSize * 2; // e.g., 3.90 inches → 7.8
      chartMaxSize = maxSize * 2; // e.g., 5.94 inches → 11.88
    }

    setOptions((prevOptions) => ({
      ...prevOptions,
      plugins: {
        ...prevOptions.plugins,
        greenBandPlugin: {
          ...prevOptions.plugins.greenBandPlugin,
          show: showPercentage,
          minSize: chartMinSize,
          maxSize: chartMaxSize,
        },
      },
    }));
  }, [showPercentage, minSize, maxSize, isImperial]);

  const handleDateChange = (dates) => {
    if (dates) {
      setSelectedDates(dates);
      updateGraphData(dates[0], dates[1]);
    }
  };

  const toggleLineVisibility = (label) => {
    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [label]: !prevVisibility[label],
    }));
  };

  const updateAverageData = (startDate, endDate) => {
    const start = startDate.format(DATE_FORMAT);
    const end = endDate.format(DATE_FORMAT);

    const filteredData = Object.entries(graphData)
      .filter(([date]) => date >= start && date <= end)
      .map(([date, values]) => {
        const avgLength = isImperial ? cmToInches(values.average_length_cm) : values.average_length_cm;
        const stdDev = isImperial ? cmToInches(values.std_dev_cm) : values.std_dev_cm;
        return {
          date: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          averageLength: avgLength,
          stdDev: stdDev,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          tension: 0.3,
        };
      })
      .sort((a, b) =>
        dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
          dayjs(b.date, GRAPH_UI_DATE_FORMAT),
        ),
      ); // Sort by date

    setAverageData(filteredData);
  };

  const getLengthValue = (item) => {
    if (typeof item === "number") return item;
    if (item && typeof item === "object" && "length_cm" in item)
      return item.length_cm;
    return null; // skip if not valid
  };

  const updateGraphData = (startDate, endDate) => {
    const start = startDate.format(DATE_FORMAT);
    const end = endDate.format(DATE_FORMAT);

    const filteredData = Object.entries(graphData)
      .filter(([date]) => date >= start && date <= end)
      .sort((a, b) => dayjs(a[0], DATE_FORMAT).diff(dayjs(b[0], DATE_FORMAT))) // Sort entries by date
      .map(([date, values], index) => {
        const rawApiSizes = (values?.[KEY_IN_DATA] || [])
          .map((item) => getLengthValue(item))
          .map((value) => (value === null || value === "" ? NaN : Number(value)))
          .filter((value) => Number.isFinite(value));
        const rawSizes = isImperial ? rawApiSizes.map((s) => cmToInches(s)) : rawApiSizes;

        const binCounts = Array.from({ length: BIN_END }, () => 0);
        rawApiSizes.forEach((size) => {
          if (size < 0 || size >= 24) return;
          const binIndex = isImperial
            ? Math.floor(cmToInches(size) * 2)
            : Math.floor(size);
          if (binIndex >= 0 && binIndex < BIN_END) {
            binCounts[binIndex]++;
          }
        });
        const hist = binCounts;
        const totalProcessedHeadCount = hist.reduce(
          (partialSum, a) => partialSum + a,
          0,
        );
        const histogramsPercentage =
          totalProcessedHeadCount > 0
            ? hist.map((value) => (value / totalProcessedHeadCount) * 100)
            : hist.map(() => 0);

        const cmBasedBinCounts = Array.from({ length: 24 }, () => 0);
        rawApiSizes.forEach((size) => {
          if (size < 0 || size >= 24) return;
          const binIndex = Math.floor(size);
          if (binIndex >= 0 && binIndex < 24) {
            cmBasedBinCounts[binIndex]++;
          }
        });
        const cmBasedTotal = cmBasedBinCounts.reduce((sum, a) => sum + a, 0);
        const cmBasedHistogramPercentage =
          cmBasedTotal > 0
            ? cmBasedBinCounts.map((value) => (value / cmBasedTotal) * 100)
            : cmBasedBinCounts.map(() => 0);

        return {
          label: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          data: histogramsPercentage,
          cmBasedData: cmBasedHistogramPercentage,
          fill: false,
          borderColor: graphColors[index % graphColors.length],
          backgroundColor: graphColors[index % graphColors.length],
          tension: 0.3,
          spanGaps: true,
          rawSizes,
        };
      });

    setFilteredGraphData(filteredData);

    // Initialize visibility: all lines visible by default
    setVisibility({
      ...filteredData.reduce((acc, dataset) => {
        acc[dataset.label] = true; // All distribution lines visible
        return acc;
      }, {}),
      [isImperial ? "Average Broccoli Head Size (inches)" : "Average Broccoli Head Size (cm)"]: true,
      "Standard Deviation": true, // Visible by default
    });

    updateAverageData(startDate, endDate);
    setShowPercentage(false);
  };

  useEffect(() => {
    if (dateList && dateList.length > 0) {
      const sortedDates = dateList.sort((a, b) => dayjs(a).diff(dayjs(b)));
      const initialStartDate = dayjs(sortedDates[0]);
      const initialEndDate = dayjs(sortedDates[sortedDates.length - 1]);
      setSelectedDates([initialStartDate, initialEndDate]);
      updateGraphData(initialStartDate, initialEndDate);
    }
  }, [graphData, dateList, isImperial]);

  return (
    <div className={`flex w-full flex-col ${isFullscreen ? "h-full min-h-0" : ""}`}>
      <h2 className={`font-bold ${isFullscreen ? "text-xl" : "text-2xl"}`}>
        Head Size Distribution
      </h2>
      <div
        className={`graph-header w-full flex flex-wrap justify-between items-center sm:flex-col ${
          isFullscreen ? "mb-2 gap-2" : "mb-4"
        }`}
      >
        <div className="custom-legend flex flex-wrap gap-3 sm:flex-col">
          {filteredGraphData.map((dataset, index) => (
            <div
              key={dataset.label}
              className="flex items-center mb-1 cursor-pointer"
              onClick={() => toggleLineVisibility(dataset.label)}
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor: graphColors[index % graphColors.length],
                  opacity: visibility[dataset.label] ? 1 : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor: graphColors[index % graphColors.length],
                    opacity: visibility[dataset.label] ? 1 : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span style={{ opacity: visibility[dataset.label] ? 1 : 0.3 }}>
                {dataset.label}
              </span>
            </div>
          ))}
        </div>
        <div
          className={`flex items-center gap-2 ${
            isFullscreen
              ? "w-full justify-end sm:w-auto sm:justify-start"
              : "ml-auto"
          }`}
        >
          {isSuperUser && (
            <>
              <span
                className="text-xs font-semibold"
                style={{ color: COLORS.darkerGray }}
              >
                Graph Type:
              </span>
              <Select
                value={graphType}
                onChange={setGraphType}
                options={graphTypeOptions}
                className="w-[210px]"
              />
            </>
          )}
          <RangePicker
            defaultValue={selectedDates}
            value={selectedDates}
            onChange={handleDateChange}
            className="date flex items-center justify-center"
            format={UI_DATE_FORMAT}
          />
        </div>
      </div>
      {isSuperUser && (
        <Alert
          type="info"
          showIcon={false}
          className={`${isFullscreen ? "mb-2" : "mb-3"} !py-1`}
          style={{ border: "none", backgroundColor: "transparent" }}
          message={
            <div className="text-xs leading-5" style={{ color: COLORS.primaryBlue }}>
              <span className="font-semibold">{activeGraphInfo.short}</span>{" "}
              <span>{activeGraphInfo.relevance}</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                title="How this graph is calculated"
                content={
                  <div className="max-w-[380px] text-xs leading-5">
                    <div className="mb-1 font-semibold">{activeGraphInfo.relevance}</div>
                    {activeGraphInfo.details.map((detail) => (
                      <div key={detail}>{detail}</div>
                    ))}
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Graph calculation details"
                  icon={<InfoCircleOutlined style={{ color: COLORS.primaryBlue }} />}
                  className="!ml-1 !h-auto !w-auto !min-w-0 !p-0 align-middle"
                />
              </Popover>
            </div>
          }
        />
      )}
      <div
        className={`w-full ${
          isFullscreen
            ? "min-h-0 flex-1 overflow-x-auto overflow-y-hidden"
            : "overflow-x-auto"
        }`}
      >
        <div className={isFullscreen ? "h-full min-w-[760px]" : "min-w-[640px]"}>
          {graphType === "heatmap" ? (
            <Scatter data={heatmapChartData} options={heatmapOptions} />
          ) : graphType === "quantile" ? (
            <Line data={quantileChartData} options={quantileOptions} />
          ) : graphType === "specMix" ? (
            <Line data={specMixChartData} options={specMixOptions} />
          ) : graphType === "ecdf" ? (
            <Line data={ecdfChartData} options={ecdfOptions} />
          ) : graphType === "delta" ? (
            <Line data={deltaChartData} options={deltaOptions} />
          ) : graphType === "kpi" ? (
            <Line data={kpiChartData} options={kpiOptions} />
          ) : (
            <Line
              data={chartData}
              options={chartOptions}
              plugins={[greenBandPlugin]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { DistributionGraph };
